/** @format */

import { memo } from 'react'

import styles from 'dir/styles/courses.module.css'

export const _2 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>2 - Абсолютные и относительные единицы CSS</h2>
    <p className={styles.description}>
      Для того чтобы изображение корректно отображалось на экранах любого разрешения используется понятие CSS-пикселя.
      Физический пиксель может быть любого размера, (в экранах retina физический пиксель меньше обычного и ОС формирует
      группы, с которыми программы, в том числе и браузер, работает как с обычными пикселями).{' '}
    </p>
    <p className={styles.description}>
      Заботу о связи пиксела CSS и физического пиксела берет на себя операционная система. Разработчик браузерного
      приложения может воспринимать CSS пиксель как наименьший элемент, который браузер способен отобразить.{' '}
    </p>
    <p className={styles.description}>
      При масштабе браузера в 100%, один CSS-пиксель точно равен одному пикселю устройства. Если масштаб изменяется, то
      браузер делает пересчет размера пикселя, а не их количества. На практике это приводит к тому, что 5 пикселей при
      уменьшении масштаба в два раза (zoom 50%) изменят свои размеры до 2,5 пикселей и браузер вынужден будет округлить
      это значение до 3px. В результате размер элемента изменится - к размерам добавится 1 пиксель и это может разрушить
      верстку (элемент может перескочить на другой ряд).{' '}
    </p>
    <p className={styles.description}>
      Чтобы избежать подобной проблемы были созданы относительные величины. Браузер при расчете в относительных единицах
      сохранит пропорции так, что лишний пиксель не сможет появиться. Проблемой остается свойство border, т.к. только
      этому свойству box-model нельзя задать относительный размер.{' '}
    </p>
    <p className={styles.notificationWarn}>
      Проблема фиксированных единиц для border имеет несолько решений. Одно из них это использование свойства{' '}
      <code className={styles.code}>box-sizing: border-box;</code> Это свойство переопределит способ расчета размера
      блока.{' '}
    </p>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.1 - Абсолютные: px mm, cm, pt, pc</h3>
      <p className={styles.description}>
        Здесь все просто - надо использовать px , кроме случая верстки страницы для печатного устройства (например,
        какие-либо бланки). Тогда уместно использовать pt (типографский пункт) или pc (типографская пика). Единицы cm и
        mm присутствуют в стандарте только для поддержки старых сайтов.
      </p>
      <table className={styles.table}>
        <caption>Формулы перерасчета CSS пикселей (не путать с физическими пикселями)</caption>
        <thead>
          <tr>
            <th>unit</th>
            <th>px</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>1mm</td>
            <td>3.8px</td>
          </tr>
          <tr>
            <td>1cm</td>
            <td>38px</td>
          </tr>
          <tr>
            <td>1pt</td>
            <td>4/3px</td>
          </tr>
          <tr>
            <td>1pc</td>
            <td>16px</td>
          </tr>
        </tbody>
      </table>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.2 - Относитльеные: %, em и rem, vh и vw</h3>
      <p className={styles.description}>
        Использование em слишком сложно - это свойство использует для расчета своей величины размер шрифта родительского
        контейнера, что затрудняет модификацию и поддержку кода. Величина vw плохо поддерживается браузерами. Поэтому
        наиболее используемые - это %, rem и vh. Рассмотрим их подробнее.{' '}
      </p>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>2.2.1 - %</h4>
        <p className={styles.description}>
          Нужно всегда выяснять от какого значения будет вычисляться размер. Наиболее трудным является вычисление высоты
          в box-model:{' '}
        </p>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Чтобы получить величину в % для</th>
              <th>за основу вычисления надо взять ...</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>height</td>
              <td>
                <b>height</b> родительского контейнера
              </td>
            </tr>
            <tr>
              <td>padding и margin</td>
              <td>
                <b>width</b> родительского контейнера
              </td>
            </tr>
            <tr>
              <td>border</td>
              <td>
                <b>не использует %</b>
              </td>
            </tr>
          </tbody>
        </table>
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>2.2.2 - rem</h4>
        <p className={styles.description}>
          Только размер шрифта, заданный в теге <code className={styles.code}>html</code> может изменить значения{' '}
          <code className={styles.code}>rem</code>. По умолчанию браузер устанавливает величину шрифта в{' '}
          <code className={styles.code}>16px</code> (хотя это может изменить пользователь). Поэтому при использовании{' '}
          <code className={styles.code}>rem</code> обязательно укажите размер шрифта в{' '}
          <code className={styles.code}>html</code> явно. При использовании на странице rem не наследуется ни от чего
          более. Поэтому задав в <code className={styles.code}>html</code> фонт размером{' '}
          <code className={styles.code}>20px</code> можно расчитывать, что <code className={styles.code}>2rem</code>{' '}
          будут всегда равны <code className={styles.code}>2 * 20px = 40px</code>.{' '}
        </p>
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>2.2.3 - vh</h4>
        <p className={styles.description}>
          Размер высоты <code className={styles.code}>viewport</code> или, проще говоря -{' '}
          <code className={styles.code}>100vh</code> заполнит экран по высоте полностью. Эту особенность данной величины
          используют в случаях, когда требуется разместить контент на весь экран (например, такой прием часто используют
          для фотографий на лендинг страницах). Использование меньших размеров этого значения затрудняет тот факт, что
          неизвестно количество пикселей, оказавшееся в <code className={styles.code}>50vh</code>. Очевидно, что это
          будет половина экрана. Но это <code className={styles.code}>384px</code> против{' '}
          <code className={styles.code}>512px</code> для высоты экранов <code className={styles.code}>768px</code> и{' '}
          <code className={styles.code}>1024px</code>. Такой разброс не подходит для формирования сложных конструкций.
        </p>
      </section>
    </section>
  </section>
))
